import { KeyboardEvent, useState } from 'react';

export interface Industry { name: string; notes?: string }

// Structured industry list where each tag can carry its own targeting notes.
// Add by pasting a comma-separated list (splits, trims, dedupes) or one at a time
// with +. Each row has an optional notes field (buyer titles, disqualifiers,
// anything specific to that vertical) that the search folds in when it's picked.
// Notes commit on blur; add/remove commit immediately.
export function IndustryEditor({
  value,
  onChange,
  disabled,
}: {
  value: Industry[];
  onChange: (next: Industry[]) => void;
  disabled?: boolean;
}) {
  const [list, setList] = useState<Industry[]>(value);
  const [input, setInput] = useState('');

  function commit(next: Industry[]) { setList(next); onChange(next); }

  function addFromInput() {
    const parts = input.split(',').map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const lower = new Set(list.map((i) => i.name.toLowerCase()));
    const merged = [...list];
    for (const p of parts) {
      if (!lower.has(p.toLowerCase())) { merged.push({ name: p, notes: '' }); lower.add(p.toLowerCase()); }
    }
    commit(merged);
    setInput('');
  }
  function remove(name: string) { commit(list.filter((i) => i.name !== name)); }
  function typeNotes(name: string, notes: string) { setList(list.map((i) => (i.name === name ? { ...i, notes } : i))); }
  function onKey(e: KeyboardEvent<HTMLInputElement>) { if (e.key === 'Enter') { e.preventDefault(); addFromInput(); } }

  return (
    <div className="space-y-2">
      {list.map((i) => (
        <div key={i.name} className="rounded-xl border border-line bg-surface-2 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-head text-sm font-semibold text-soft">{i.name}</span>
            {!disabled && <button type="button" onClick={() => remove(i.name)} className="text-muted hover:text-danger" aria-label={`Remove ${i.name}`}>×</button>}
          </div>
          {!disabled && (
            <input
              className="input mt-1.5 py-1.5 text-sm"
              value={i.notes || ''}
              onChange={(e) => typeNotes(i.name, e.target.value)}
              onBlur={() => onChange(list)}
              placeholder="Optional notes for this vertical — buyer titles, disqualifiers, specifics…"
            />
          )}
        </div>
      ))}
      {!disabled && (
        <div className="flex gap-2">
          <input className="input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} placeholder="Add industries — type or paste a comma-separated list…" />
          <button type="button" onClick={addFromInput} className="btn-ghost shrink-0 px-3" aria-label="Add industry">+</button>
        </div>
      )}
      {list.length === 0 && disabled && <p className="text-sm text-muted">No industries yet.</p>}
    </div>
  );
}
